import React, { createContext, useContext, useState, useCallback } from 'react';
import logger from '../../utils/logger';

// Define the tab state structure
interface TabState {
    functionExplorer: {
        selectedContract: any;
        selectedFunction: string | null;
        functionInputs: { [key: string]: any[] };
        results: { [key: string]: string };
        executionStatus: string;
        showSignatureFlow: boolean;
        owners: string[];
    };
    transactionBuilder: {
        selectedContract: any;
        selectedFunction: string | null;
        functionInputs: { [key: string]: string[] };
        nestedData: string | null;
        showSignatureFlow: boolean;
        executionStatus: string;
        transactionValue: string;
        owners: string[];
    };
}

// Initial state
const initialTabState: TabState = {
    functionExplorer: {
        selectedContract: null,
        selectedFunction: null,
        functionInputs: {},
        results: {},
        executionStatus: '',
        showSignatureFlow: false,
        owners: [],
    },
    transactionBuilder: {
        selectedContract: null,
        selectedFunction: null,
        functionInputs: {},
        nestedData: null,
        showSignatureFlow: false,
        executionStatus: '',
        transactionValue: '',
        owners: [],
    },
};

interface TabStateContextType {
    tabState: TabState;

    // Function Explorer methods
    setFunctionExplorerState: (updates: Partial<TabState['functionExplorer']>) => void;
    getFunctionExplorerState: () => TabState['functionExplorer'];

    // Transaction Builder methods
    setTransactionBuilderState: (updates: Partial<TabState['transactionBuilder']>) => void;
    getTransactionBuilderState: () => TabState['transactionBuilder'];

    // Utility methods
    resetTabState: (tab: keyof TabState) => void;
    clearAllTabStates: () => void;
}

const TabStateContext = createContext<TabStateContextType | undefined>(undefined);

export const TabStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [tabState, setTabState] = useState<TabState>(() => {
        // CRITICAL: Check for localStorage backup FIRST (survives Coinbase page reload)
        try {
            const backupState = localStorage.getItem('tabState_backup');
            if (backupState) {
                const parsedBackup = JSON.parse(backupState);
                logger.log('🔄 RECOVERY: Restored tab state from localStorage backup (Coinbase reload recovery)');

                // Set recovery flag to prevent cleanup in FunctionExplorer
                localStorage.setItem('just_recovered', 'true');

                // Auto-clear recovery flag after 2 seconds
                setTimeout(() => {
                    localStorage.removeItem('just_recovered');
                    logger.log('🔄 RECOVERY: Recovery flag cleared');
                }, 2000);

                // Clean up the backup after successful recovery
                localStorage.removeItem('tabState_backup');

                // Also restore to sessionStorage for normal operation
                sessionStorage.setItem('tabState', backupState);

                return { ...initialTabState, ...parsedBackup };
            }
        } catch (error) {
            logger.error('Error restoring tab state from localStorage backup:', error);
        }

        // Try to restore state from sessionStorage on initialization (normal operation)
        try {
            const savedState = sessionStorage.getItem('tabState');
            if (savedState) {
                const parsedState = JSON.parse(savedState);
                logger.log('Restored tab state from sessionStorage');
                return { ...initialTabState, ...parsedState };
            }
        } catch (error) {
            logger.error('Error restoring tab state from sessionStorage:', error);
        }
        return initialTabState;
    });

    // Save state to sessionStorage whenever it changes
    const saveStateToStorage = useCallback((newState: TabState) => {
        try {
            sessionStorage.setItem('tabState', JSON.stringify(newState));
        } catch (error) {
            logger.error('Error saving tab state to sessionStorage:', error);
        }
    }, []);

    // Function Explorer state management
    const setFunctionExplorerState = useCallback((updates: Partial<TabState['functionExplorer']>) => {
        setTabState(prevState => {
            const newState = {
                ...prevState,
                functionExplorer: {
                    ...prevState.functionExplorer,
                    ...updates,
                },
            };
            saveStateToStorage(newState);
            logger.log('Updated Function Explorer state:', updates);
            return newState;
        });
    }, [saveStateToStorage]);

    const getFunctionExplorerState = useCallback(() => {
        return tabState.functionExplorer;
    }, [tabState.functionExplorer]);

    // Transaction Builder state management
    const setTransactionBuilderState = useCallback((updates: Partial<TabState['transactionBuilder']>) => {
        setTabState(prevState => {
            const newState = {
                ...prevState,
                transactionBuilder: {
                    ...prevState.transactionBuilder,
                    ...updates,
                },
            };
            saveStateToStorage(newState);
            logger.log('Updated Transaction Builder state:', updates);
            return newState;
        });
    }, [saveStateToStorage]);

    const getTransactionBuilderState = useCallback(() => {
        return tabState.transactionBuilder;
    }, [tabState.transactionBuilder]);

    // Utility methods
    const resetTabState = useCallback((tab: keyof TabState) => {
        setTabState(prevState => {
            const newState = {
                ...prevState,
                [tab]: initialTabState[tab],
            };
            saveStateToStorage(newState);
            logger.log(`Reset ${tab} state`);
            return newState;
        });
    }, [saveStateToStorage]);

    const clearAllTabStates = useCallback(() => {
        setTabState(initialTabState);
        try {
            sessionStorage.removeItem('tabState');
            logger.log('Cleared all tab states');
        } catch (error) {
            logger.error('Error clearing tab state from sessionStorage:', error);
        }
    }, []);

    const contextValue: TabStateContextType = {
        tabState,
        setFunctionExplorerState,
        getFunctionExplorerState,
        setTransactionBuilderState,
        getTransactionBuilderState,
        resetTabState,
        clearAllTabStates,
    };

    return (
        <TabStateContext.Provider value={contextValue}>
            {children}
        </TabStateContext.Provider>
    );
};

export const useTabState = () => {
    const context = useContext(TabStateContext);
    if (context === undefined) {
        throw new Error('useTabState must be used within a TabStateProvider');
    }
    return context;
};

// Convenience hooks for specific tabs
export const useFunctionExplorerState = () => {
    const { getFunctionExplorerState, setFunctionExplorerState } = useTabState();
    return {
        state: getFunctionExplorerState(),
        setState: setFunctionExplorerState,
    };
};

export const useTransactionBuilderState = () => {
    const { getTransactionBuilderState, setTransactionBuilderState } = useTabState();
    return {
        state: getTransactionBuilderState(),
        setState: setTransactionBuilderState,
    };
};
